"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BodyClassObserverHelper = exports.AttributeObserverHelper = void 0;
/**
 * AttributeObserverHelper - A general utility to observe attribute changes on DOM elements
 *
 * This helper provides a clean way to watch for attribute changes on any DOM element
 * and execute callbacks when changes occur.
 */
class AttributeObserverHelper {
    constructor(shadowRoot) {
        this.callbacks = new Set();
        this.shadowRoot = shadowRoot;
        this._currentValue = this.getInitialValue();
    }
    /**
     * Get the current observed value
     */
    get currentValue() {
        return this._currentValue;
    }
    /**
     * Get the initial value
     * This method can be overridden by subclasses if needed
     */
    getInitialValue() {
        return this.extractValue();
    }
    /**
     * Get the document element (respects shadow root context)
     */
    getDocument() {
        var _a;
        if ((_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.ownerDocument) {
            return this.shadowRoot.ownerDocument;
        }
        return document;
    }
    /**
     * Check and update the current value
     */
    checkValue() {
        const newValue = this.extractValue();
        if (this.hasValueChanged(newValue, this._currentValue)) {
            const oldValue = this._currentValue;
            this._currentValue = newValue;
            this.notifyCallbacks(newValue, oldValue);
        }
    }
    /**
     * Determine if the value has changed
     * This method can be overridden for custom comparison logic
     */
    hasValueChanged(newValue, oldValue) {
        return newValue !== oldValue;
    }
    /**
     * Notify all registered callbacks of value change
     */
    notifyCallbacks(newValue, oldValue) {
        this.callbacks.forEach(callback => {
            try {
                callback(newValue);
            }
            catch (error) {
                console.error('Error in attribute observer callback:', error);
            }
        });
    }
    /**
     * Start observing attribute changes
     */
    start() {
        if (this.observer) {
            console.warn('AttributeObserver is already running');
            return;
        }
        this.targetElement = this.getTargetElement();
        if (!this.targetElement) {
            console.error('Could not find target element for attribute observation');
            return;
        }
        // Check initial state
        this.checkValue();
        // Set up observer
        this.observer = new MutationObserver(() => {
            this.checkValue();
        });
        this.observer.observe(this.targetElement, {
            attributes: true,
            attributeFilter: this.getObservedAttributes()
        });
    }
    /**
     * Stop observing and clean up resources
     */
    stop() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = undefined;
        }
        this.targetElement = undefined;
        this.callbacks.clear();
    }
    /**
     * Subscribe to value changes
     * @param callback Function to call when value changes
     * @returns Unsubscribe function
     */
    subscribe(callback) {
        this.callbacks.add(callback);
        // Immediately call with current value
        callback(this._currentValue);
        // Return unsubscribe function
        return () => {
            this.callbacks.delete(callback);
        };
    }
    /**
     * Unsubscribe a specific callback
     */
    unsubscribe(callback) {
        this.callbacks.delete(callback);
    }
    /**
     * Get the number of active subscribers
     */
    get subscriberCount() {
        return this.callbacks.size;
    }
}
exports.AttributeObserverHelper = AttributeObserverHelper;
/**
 * BodyClassObserverHelper - A specific implementation for observing body class changes
 */
class BodyClassObserverHelper extends AttributeObserverHelper {
    getTargetElement() {
        return this.getDocument().body;
    }
    getObservedAttributes() {
        return ['class'];
    }
    extractValue() {
        const body = this.getTargetElement();
        if (!body)
            return [];
        return Array.from(body.classList);
    }
    hasValueChanged(newValue, oldValue) {
        if (newValue.length !== oldValue.length)
            return true;
        return !newValue.every((cls, index) => cls === oldValue[index]);
    }
    /**
     * Check if a specific class is present
     */
    hasClass(className) {
        return this.currentValue.includes(className);
    }
    /**
     * Subscribe to changes for a specific class
     */
    subscribeToClass(className, callback) {
        return this.subscribe((classes) => {
            callback(classes.includes(className));
        });
    }
}
exports.BodyClassObserverHelper = BodyClassObserverHelper;
